import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Button, Badge, Table, Alert, Form } from 'react-bootstrap';
import { ArrowLeft, Calendar, FileText, Package, Check, X, AlertCircle, Truck, Download, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDeliveryNotesByOrder, getOrderById, updateOrderStatus } from '../../api/orders';
import useAuth from '../../hooks/useAuth';
import Loading from '../common/Loading';
import GenererPdfDevis from './GenererPdfDevis';

const OrderDetail = ({ orderId, editMode, onCancel, onSuccess }) => {
    const { isAdmin, isManager, isSalesRep, isDistributor } = useAuth();

    const hasStockAccess = isAdmin || isManager || isSalesRep;
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [commentairerejet, setCommentairerejet] = useState('');

    const [hasDeliveries, setHasDeliveries] = useState(false);
    const [deliveryNotes, setDeliveryNotes] = useState([]);

    const canUpdateStatus = isAdmin || isManager;

    // --- Navigation + téléchargement PDF ---
    const navigate = useNavigate();
    const [preparingDownload, setPreparingDownload] = useState(false);
    const downloadFiredRef = useRef(false);

    // Infos société transmises au générateur PDF (cohérent avec OrderList)
    const company = {
        name: 'SENEGAL SOPLAST SARL',
        address: {
            street: 'Zone Industrielle de Thiès',
            city: 'Thiès, Dakar, Sénégal',
        },
        phone1: '+221 33 888 88 88',
        email: 'contact@soplast.sn',
    };

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                setLoading(true);
                setError('');

                const orderData = await getOrderById(orderId);
                console.log('Fetched order data:', orderData);

                setOrder(orderData);
                setSelectedStatus(orderData.status);
                // If there's already a rejection comment, load it
                if (orderData.commentairerejet) {
                    setCommentairerejet(orderData.commentairerejet);
                }
            } catch (err) {
                setError('Failed to load order data. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (orderId) {
            fetchOrder();
        }
    }, [orderId]);

    useEffect(() => {
        const checkDeliveries = async () => {
            try {
                const notes = await getDeliveryNotesByOrder(orderId);
                const list = Array.isArray(notes) ? notes : [];
                setDeliveryNotes(list);
                setHasDeliveries(list.length > 0);
            } catch {
                setDeliveryNotes([]);
                setHasDeliveries(false);
            }
        };
        if (orderId) checkDeliveries();
    }, [orderId]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount, withSymbol = true) => {
        if (!amount && amount !== 0) return withSymbol ? '0 FCFA' : '0';

        // Format avec un point comme séparateur de milliers et virgule pour décimales
        const parts = Number(amount).toFixed(0).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const formattedValue = parts.join(',');

        // Ajout du symbole de devise si demandé
        return withSymbol ? formattedValue + ' FCFA' : formattedValue;
    };

    const calculateSubTotal = () => {
        if (!order || !order.items) return 0;
        return order.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    };

    const calculateVatAmount = () => {
        const subTotal = calculateSubTotal();
        return subTotal * 0.18; // 18% TVA
    };

    const calculateTotal = () => {
        const subTotal = calculateSubTotal();
        const vatAmount = order.vatApplicable ? calculateVatAmount() : 0;
        return subTotal + vatAmount;
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning">En attente</Badge>;
            case 'CONFIRMED':
                return <Badge bg="primary">Confirmé</Badge>;
            case 'DELIVERED':
                return <Badge bg="success">Livré</Badge>;
            case 'REJECTED':
                return <Badge bg="danger">Rejeté</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    // Get valid next status options based on current status
    const getValidStatusOptions = (currentStatus) => {
        switch (currentStatus) {
            case 'PENDING':
                return [
                    { value: 'PENDING', label: 'En attente' },
                    { value: 'CONFIRMED', label: 'Confirmée' },
                    { value: 'REJECTED', label: 'Rejeté' }
                ];
            case 'CONFIRMED':
                return [
                    { value: 'CONFIRMED', label: 'Confirmée' },
                    { value: 'REJECTED', label: 'Rejeté' }
                ];
            case 'DELIVERED':
                // No transitions allowed from DELIVERED
                return [
                    { value: 'DELIVERED', label: 'Livré' }
                ];
            case 'REJECTED':
                // No transitions allowed from REJECTED
                return [
                    { value: 'REJECTED', label: 'Rejeté' }
                ];
            default:
                return [];
        }
    };

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
    };

    const handleRejectionCommentChange = (e) => {
        setCommentairerejet(e.target.value);
    };

    const handleUpdateStatus = async () => {
        if (selectedStatus === order.status) return;

        try {
            setUpdating(true);
            setError('');

            // Include rejection comment if status is REJECTED
            const requestData = {
                status: selectedStatus
            };

            if (selectedStatus === 'REJECTED') {
                if (!commentairerejet.trim()) {
                    setError('Veuillez fournir un motif de rejet.');
                    setUpdating(false);
                    return;
                }
                requestData.commentairerejet = commentairerejet;
            }

            await updateOrderStatus(orderId, requestData);

            // Update local order status
            setOrder({
                ...order,
                status: selectedStatus,
                ...(selectedStatus === 'REJECTED' && { commentairerejet })
            });

            setSuccess(`Le statut de la commande a été mis à jour avec succès en "${selectedStatus}" !`);

            // Call success callback after delay
            setTimeout(() => {
                if (onSuccess) onSuccess();
            }, 1500);
        } catch (err) {
            setError('Échec de la mise à jour du statut de la commande. Veuillez vérifier les stocks des produits.');
            console.error(err);
        } finally {
            setUpdating(false);
        }
    };

    // --- Téléchargement du PDF de la commande ---
    const handleDownloadClick = () => {
        downloadFiredRef.current = false;
        setPreparingDownload(true);
    };

    // Appelé par GenererPdfDevis une fois le blob prêt. Différé hors phase de rendu.
    const triggerBlobDownload = (blob) => {
        if (downloadFiredRef.current) return;
        downloadFiredRef.current = true;
        setTimeout(() => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const docType = order.status === 'PENDING' ? 'devis' : 'commande';
            link.download = `${docType}_${order.orderNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setPreparingDownload(false);
        }, 0);
    };

    // --- Créer un bon de livraison pour cette commande ---
    const handleCreateDelivery = () => {
        // DNManagement (route /delivery-notes) doit lire location.state.createForOrderId
        // pour ouvrir directement le formulaire de création avec cette commande.
        navigate('/delivery-notes', { state: { createForOrderId: order.id } });
    };

    if (loading) {
        return <Loading message="Chargement des détails de la commande..." />;
    }

    // if (error) {
    //     return <Alert variant="danger">{error}</Alert>;
    // }

    if (!order) {
        return <Alert variant="warning">Commande non trouvée</Alert>;
    }

    const validStatusOptions = getValidStatusOptions(order.status);
    const canChangeStatus = ['PENDING', 'CONFIRMED'].includes(order.status);

    // Calcul du sous-total et de la TVA
    const subTotal = calculateSubTotal();
    const vatAmount = order.vatApplicable ? calculateVatAmount() : 0;
    const totalAmount = calculateTotal();

    return (
        <div>
            {error && <Alert variant="danger">{error}</Alert>}

            {/* En-tête : retour à gauche, actions à droite, empilées sous md */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-4">
                <Button
                    variant="outline-secondary"
                    onClick={onCancel}
                >
                    <ArrowLeft size={16} className="me-1" />
                    Retour aux commandes
                </Button>

                <div className="d-flex flex-wrap align-items-center gap-2">
                    {success && (
                        <Alert variant="success" className="mb-0 py-2 px-3">{success}</Alert>
                    )}

                    {/* Télécharger le PDF (devis / commande) */}
                    <Button
                        variant="outline-primary"
                        onClick={handleDownloadClick}
                        disabled={preparingDownload}
                    >
                        <Download size={16} className="me-1" />
                        {preparingDownload ? 'Préparation…' : 'Télécharger'}
                    </Button>

                    {/* Créer un bon de livraison — commandes confirmées uniquement */}
                    {canUpdateStatus && order.status === 'CONFIRMED' && (
                        <Button
                            variant="primary"
                            onClick={handleCreateDelivery}
                        >
                            <Truck size={16} className="me-1" />
                            Créer une livraison
                        </Button>
                    )}
                </div>
            </div>

            {/* Générateur PDF masqué, monté à la demande pour déclencher le téléchargement */}
            {preparingDownload && (
                <GenererPdfDevis
                    order={order}
                    company={company}
                    onDownload={triggerBlobDownload}
                />
            )}

            <Row className="mb-4">
                <Col lg={8}>
                    <Card className="shadow-sm mb-4">
                        <Card.Header className="bg-white">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Commande #{order.orderNumber}</h5>
                                {getStatusBadge(order.status)}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="mb-3">
                                        <div className="text-muted mb-1">Client</div>
                                        <div className="fw-bold">{order.partner.name}</div>
                                        <small className="text-muted">
                                            {order.partner.partnerType === 'DISTRIBUTOR' ? 'Distributeur' : 'Détaillant'}
                                        </small>
                                    </div>

                                    {order.referenceClient && (
                                        <div className="mb-3">
                                            <div className="text-muted mb-1">Référence commande client</div>
                                            <div className="fw-bold">{order.referenceClient}</div>
                                        </div>
                                    )}

                                    <div>
                                        <div className="text-muted mb-1">Statut TVA</div>
                                        <div>
                                            {order.vatApplicable ? (
                                                <Badge bg="info">Avec TVA</Badge>
                                            ) : (
                                                <Badge bg="warning">Exonéré de TVA</Badge>
                                            )}
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3 d-flex align-items-start">
                                        <Calendar size={18} className="me-2 text-muted mt-1" />
                                        <div>
                                            <div className="text-muted">Date commande</div>
                                            <div>{formatDateTime(order.orderDate)}</div>
                                        </div>
                                    </div>

                                    {order.deliveryDate && (
                                        <div className="d-flex align-items-start mb-3">
                                            <Package size={18} className="me-2 text-muted mt-1" />
                                            <div>
                                                <div className="text-muted">Date de livraison</div>
                                                <div>{formatDate(order.deliveryDate)}</div>
                                            </div>
                                        </div>
                                    )}

                                    {order.deliveryAddress && (
                                        <div className="d-flex align-items-start">
                                            <List size={18} className="me-2 text-muted mt-1" />
                                            <div>
                                                <div className="text-muted">Adresse de livraison</div>
                                                <div>{order.deliveryAddress}</div>
                                            </div>
                                        </div>
                                    )}

                                    {order.status === 'REJECTED' && (
                                        <div className="mt-3">
                                            <div className="text-muted mb-1">Motif de rejet</div>
                                            <div className="p-2 bg-light rounded">
                                                {order.commentairerejet || 'Aucun motif fourni'}
                                            </div>
                                        </div>
                                    )}
                                </Col>
                            </Row>


                            {order.notes && (
                                <div className="mb-4">
                                    <div className="text-muted mb-1">Notes</div>
                                    <div className="p-3 bg-light rounded">
                                        {order.notes}
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <Card className="shadow-sm">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Produits de la commande</h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Produit</th>
                                            <th className="text-center">Quantité</th>
                                            <th className="text-end">Prix Unitaire</th>
                                            <th className="text-end">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.items && order.items.map((item, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Package size={18} className="me-2 text-muted" />
                                                        <div>
                                                            <div className="fw-semibold">{item.product.name}</div>
                                                            <small className="text-muted">{item.product.sku}</small>
                                                        </div>
                                                    </div>
                                                </td>

                                            

                                                <td className="text-center">{item.quantity}</td>

                                                <td className="text-end">{formatCurrency(item.unitPrice)}</td>
                                                <td className="text-end">{formatCurrency(item.quantity * item.unitPrice)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="border-top">
                                        <tr>
                                            <td colSpan={hasStockAccess ? "4" : "3"} className="text-end fw-bold">Sous total:</td>
                                            <td className="text-end">{formatCurrency(subTotal)}</td>
                                        </tr>
                                        {order.vatApplicable && (
                                            <tr>
                                                <td colSpan={hasStockAccess ? "4" : "3"} className="text-end">TVA (18%):</td>
                                                <td className="text-end">{formatCurrency(vatAmount)}</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td colSpan={hasStockAccess ? "4" : "3"} className="text-end fw-bold">Total:</td>
                                            <td className="text-end fw-bold">{formatCurrency(totalAmount)}</td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>

                        </Card.Body>
                    </Card>

                    {/* Liste des bons de livraison associés à la commande */}
                    {deliveryNotes.length > 0 && (
                        <Card className="shadow-sm mt-4">
                            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <Truck size={18} className="me-2 text-muted" />
                                    Bons de livraison
                                </h5>
                                <Badge bg="secondary">{deliveryNotes.length}</Badge>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Référence</th>
                                                <th>Date</th>
                                                <th>Magasin</th>
                                                <th className="text-center">Qté livrée</th>
                                                <th>Statut</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {deliveryNotes.map((bl) => {
                                                const totalLivre = (bl.items || []).reduce(
                                                    (sum, it) => sum + (it.deliveredQuantity || 0), 0
                                                );
                                                return (
                                                    <React.Fragment key={bl.id}>
                                                        <tr>
                                                            <td className="fw-semibold">{bl.referenceNumber}</td>
                                                            <td>{formatDate(bl.deliveryDate || bl.createdAt)}</td>
                                                            <td>{bl.magasinName || '—'}</td>
                                                            <td className="text-center">{totalLivre}</td>
                                                            <td>{getStatusBadge(bl.status)}</td>
                                                        </tr>
                                                        {(bl.items || []).length > 0 && (
                                                            <tr>
                                                                <td colSpan={5} className="bg-light py-2">
                                                                    <small className="text-muted">
                                                                        {bl.items.map((it, i) => (
                                                                            <span key={it.id ?? i}>
                                                                                {it.productName+'-'+it.productCode} × {it.deliveredQuantity}
                                                                                {i < bl.items.length - 1 ? '  ·  ' : ''}
                                                                            </span>
                                                                        ))}
                                                                    </small>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                <Col lg={4}>
                    <Card className="shadow-sm mb-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Statut commande</h5>
                        </Card.Header>
                        <Card.Body>
                            {canUpdateStatus ? (
                                <div>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Statut actuel</Form.Label>
                                        <div className="mb-3">
                                            {getStatusBadge(order.status)}
                                        </div>

                                        {canChangeStatus && (
                                            <>
                                                <Form.Label>Modifier Statut</Form.Label>
                                                <Form.Select
                                                    value={selectedStatus}
                                                    onChange={handleStatusChange}
                                                    disabled={updating || !canChangeStatus}
                                                >
                                                    {validStatusOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </>
                                        )}
                                    </Form.Group>
                                    {order.status === 'CONFIRMED' && (
                                        <Alert variant="info" className="mt-3">
                                            <AlertCircle size={16} className="me-1" />
                                            La livraison de cette commande se fait via les bons de livraison. Elle passera automatiquement en « Livré » une fois toutes les quantités livrées.
                                        </Alert>
                                    )}

                                    {selectedStatus === 'REJECTED' && order.status !== 'REJECTED' && (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Motif de rejet</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={commentairerejet}
                                                onChange={handleRejectionCommentChange}
                                                placeholder="Veuillez indiquer le motif du rejet"
                                                required
                                            />
                                        </Form.Group>
                                    )}

                                    {canChangeStatus && (
                                        <div className="d-grid mt-3">
                                            <Button
                                                variant="primary"
                                                onClick={handleUpdateStatus}
                                                disabled={updating || selectedStatus === order.status}
                                            >
                                                {updating ? (
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                ) : (
                                                    <>
                                                        <Check size={16} className="me-1" />
                                                        Modifier
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}

                                    {!canChangeStatus && (
                                        <Alert variant="warning" className="mt-3">
                                            <AlertCircle size={16} className="me-1" />
                                            Cette commande ne peut plus être modifiée.
                                        </Alert>
                                    )}

                                    {order.status === 'DELIVERED' && (
                                        <Alert variant="info" className="mt-3">
                                            <AlertCircle size={16} className="me-1" />
                                            Le stock a été ajusté pour cette commande
                                        </Alert>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-3">
                                    <div className="mb-3">
                                        {getStatusBadge(order.status)}
                                    </div>
                                    {order.status === 'PENDING' &&
                                        <p className="text-muted mb-0">
                                            Merci pour votre commande. Veuillez patienter, SOPLAST GROUP va procéder à la validation de votre commande.
                                        </p>
                                    }
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Informations supplémentaires sur la commande si nécessaire */}
                    {order.status === 'DELIVERED' && (
                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-white">
                                <h5 className="mb-0">Informations de livraison</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-flex align-items-start mb-3">
                                    <Calendar size={18} className="me-2 text-muted mt-1" />
                                    <div>
                                        <div className="text-muted">Date de livraison</div>
                                        <div>{formatDate(order.deliveryDate)}</div>
                                    </div>
                                </div>

                                {order.deliveryAddress && (
                                    <div className="d-flex align-items-start">
                                        <Package size={18} className="me-2 text-muted mt-1" />
                                        <div>
                                            <div className="text-muted">Adresse de livraison</div>
                                            <div>{order.deliveryAddress}</div>
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default OrderDetail;